/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import isUndefined from 'lodash.isundefined';
import {
  Account,
  EntityStatus,
  GenericObject,
  Group,
  Link
} from '@ion/core/src/types/entities';
import {HierLevel} from '@ion/core/src/types/services';
import {AugmentedFormState} from '../hooks/useFormState';

// available field names for the add/edit group form ...
export enum GroupFormFields {
  Id = 'id',
  Version = 'version',
  Account = 'account',
  Name = 'name',
  Notes = 'notes',
  Assets = 'assets',
  Devices = 'devices',
  Status = 'status',
  CreatedOn = 'createdOn',
  LastModifiedOn = 'lastModifiedOn',
  AssetSwitch = 'assetSwitch',
  DeviceSwitch = 'deviceSwitch',
  Inheritable = 'inheritable'
}

// defines the driver event target type definition ...
export interface TargetType {
  name: GroupFormFields;
  value: unknown;
  required?: boolean;
}

export type SerializedArgs = {
  state: AugmentedFormState;
  account?: Account;
};

// initial form state values
const required    = {required: true};
const notRequired = {required: false};

/**
 * Provides the initial state of the driver editor form, this state includes all named
 * editor fields and their requirements, as well as any initial values required, or a
 * reference to a driver, in the case of edits.
 */
export const getInitialState = ({group}: GenericObject) => {
  return deserialize(group) || {
    [GroupFormFields.Account]     : notRequired,
    [GroupFormFields.Name]        : required,
    [GroupFormFields.Notes]       : notRequired,
    [GroupFormFields.Devices]     : notRequired,
    [GroupFormFields.Assets]      : notRequired,
    [GroupFormFields.Status]      : {...notRequired, value: EntityStatus.Enabled},
    [GroupFormFields.Inheritable] : {...notRequired, value: false}
  };
};

/**
 * Get the Values from Asset and Group members fields and filter the id with OR separator for the Devices
 * and comma separator for the assets. This will be passing in the payload as below for member and smart search.
 * Example:
 *      "memberSearch": {
 *           ...
 *           "id": "366143,394720,361569,65,394720,361535",
 *           ....
 *       }
 *       "smartSearch": {
 *         ...
 *         "queryString": "id:(7 OR 156 OR 162)"
 *         ...
 *       }
 */
export const getMembers = (value, isSmartSearch = false) => {
  return {
    value : value?.map(({id}) => id).join(isSmartSearch ? ' OR ' : ',') || '',
    name  : value?.map(({name}) => name).join(', ') || '',
    ids   : value?.map(({id}) => id)
  };
};

/**
 * Get the account link from the account object and form the account link and
 * pass it in the Group creation and update payload.
 */
export const getAccount = ({href, title}: Account): Link => {
  return {href, title, rel: 'account'};
};

/**
 * Get the Sub Group fields for Devices and Assets and pass Hier Level details in Group
 * creation and update payload.
 */
export const getSubGroups = (hasSubGroup: boolean = false) => {
  return {
    hierLevel : hasSubGroup ? HierLevel.ALL : HierLevel.NONE
  };
};

/**
 * Get the account link from the account details and form the account link
 */
export const getAccountLink = (data, isParentAccount = false, isAccountGrid = false) => {
  let link = data?.account?.href || {};
  if (isParentAccount) {
    link = data?.parent?.href;
  } else if (isAccountGrid) {
    link = data?._self?.href;
  }
  return link;
};

/**
 * Serializes the Group Editor Form Fields to their corresponding attributes required for
 * providing a valid CTC Group entity representation.
 */
export const serialize = ({state, account}: SerializedArgs): Group => {
  const {group, ...rest} = state;
  const serialized: GenericObject = {...group};
  for (const [key, {value}] of Object.entries(rest)) {
    switch (key) {
      case GroupFormFields.Devices:
        serialized[key] = getMembers(value, true);
        break;
      case GroupFormFields.Assets:
        serialized[key] = getMembers(value);
        break;
      case GroupFormFields.Account:
        serialized.account = getAccount(account);
        break;
      case GroupFormFields.Status:
        serialized[key] = value || EntityStatus.Enabled;
        break;
      case GroupFormFields.Inheritable:
        serialized[key] = value;
        break;
      case GroupFormFields.DeviceSwitch:
      case GroupFormFields.AssetSwitch:
        serialized[key] = getSubGroups(value);
        break;
      default :
        if (isUndefined(value) || value === '') {
          serialized[key] = null;
        } else {
          serialized[key] = value;
        }
        break;
    }
  }
  return serialized;
};

/**
 * Deserializes a CTC Group into an object which can be consumed by the GROUP Config Editor Form
 * Fields and used to pre-populated their respective values.
 */
export const deserialize = (group: Group) => {
  if (group) {
    return {
      [GroupFormFields.Id] : {
        name     : GroupFormFields.Id,
        value    : group[GroupFormFields.Id],
        required : false
      },
      [GroupFormFields.Name] : {
        name     : GroupFormFields.Name,
        value    : group[GroupFormFields.Name],
        required : true
      },
      [GroupFormFields.Account] : {
        name     : GroupFormFields.Account,
        value    : group[GroupFormFields.Account],
        required : false
      },
      [GroupFormFields.Notes] : {
        name     : GroupFormFields.Notes,
        value    : group[GroupFormFields.Notes],
        required : false
      },
      [GroupFormFields.Assets] : {
        name     : GroupFormFields.Assets,
        value    : group[GroupFormFields.Assets],
        required : false
      },
      [GroupFormFields.Devices] : {
        name     : GroupFormFields.Devices,
        value    : group[GroupFormFields.Devices],
        required : false
      },
      [GroupFormFields.Version] : {
        name     : GroupFormFields.Version,
        value    : group[GroupFormFields.Version],
        required : false
      },
      [GroupFormFields.Inheritable] : {
        name     : GroupFormFields.Inheritable,
        value    : group[GroupFormFields.Inheritable],
        required : false
      }
    };
  }
};
